import { useEffect, useMemo, useState } from 'react';
import { createSelector } from '@reduxjs/toolkit';
import { DateTime } from 'timezonecomplete';

import type { IOngoingShift, ITime } from '@/components/agent/dashboard/types';
import type { IDateFormatter } from '@/types/date';

import { useAppSelector, useTimezone } from '@/hooks';
import { AM_PM_TIME_SPACE_UPPERCASE_FORMAT, FULL_MONTH_DAY_YEAR_FORMAT } from '@/config/variables';
import { selectSchedulesList } from '@/store/selectors/scheduleSelectors';
import { getDateFromBlock } from '@/helpers/block';

interface IReturn {
  officialTime: ITime;
  shift?: IOngoingShift;
}

const getTimeDataNow = (formatDate: IDateFormatter): ITime => {
  const now = DateTime.nowUtc();
  const nowIsoString = now.toIsoString();

  return {
    time: formatDate(nowIsoString, AM_PM_TIME_SPACE_UPPERCASE_FORMAT),
    date: formatDate(nowIsoString, FULL_MONTH_DAY_YEAR_FORMAT),
    timestamp: now.unixUtcMillis(),
  };
};

const REFRESH_INTERVAL_MS = 1000;

const useOngoingShiftSelector = createSelector(selectSchedulesList, schedulesList => ({ schedulesList }));

const useOngoingShift = (): IReturn => {
  const { formatDateTime, timezoneName } = useTimezone();
  const [officialTime, setOfficialTime] = useState<ITime>(getTimeDataNow(formatDateTime));
  const { schedulesList } = useAppSelector(useOngoingShiftSelector);

  useEffect(() => {
    const intervalId = setInterval(() => {
      const dateNow = getTimeDataNow(formatDateTime);
      setOfficialTime(prevState => {
        if (dateNow.time !== prevState.time) return dateNow;
        return prevState;
      });
    }, REFRESH_INTERVAL_MS);
    return () => {
      clearInterval(intervalId);
    };
  });

  const shift: IOngoingShift | undefined = useMemo(() => {
    let shift = null;
    let ongoingSchedule = null;
    for (const schedule of schedulesList) {
      shift = schedule.shifts.find(({ startBlock, endBlock }) => {
        const startsAt = getDateFromBlock(startBlock, timezoneName);
        const endsAt = getDateFromBlock(endBlock + 1, timezoneName);
        return (
          startsAt &&
          endsAt &&
          officialTime.timestamp >= startsAt.getTime() &&
          officialTime.timestamp < endsAt.getTime()
        );
      });
      if (Boolean(shift)) {
        ongoingSchedule = schedule;
        break;
      }
    }
    if (shift && ongoingSchedule) {
      return {
        clientName: ongoingSchedule.clientName,
        scheduleName: ongoingSchedule.scheduleName,
        startTime: getDateFromBlock(shift.startBlock, timezoneName),
        endTime: getDateFromBlock(shift.endBlock + 1, timezoneName),
      } as IOngoingShift;
    }
    return undefined;
  }, [officialTime.timestamp, schedulesList, timezoneName]);

  return {
    officialTime,
    shift,
  };
};

export { useOngoingShift };
